/**
 * Lane FACE-EXPR step 4: the game's side of the new faces (faceExprB3.ts).
 * - `Look.restFace`: the player's resting look (appearance.ts FACES: calm, stern, gentle, cold, bright, sly), set on the
 *   player's looks at boot (`applyRestFace`) so the frame workers, which get the look itself, draw it too; any other look
 *   rests calm.
 * - `exprFor`: the expression a frame shows, from its pose (a motion's nearer key): hit -> pain, stun -> dazed,
 *   down / dead -> ko, the ท่าใหญ่ keys -> rage, the laugh / cry / cheer emotes and the victory jig, telling -> talk;
 *   null leaves today's expression (blink, strike, guard) to be mapped as before.
 * Research: docs/research/FACE_EXPR.md.
 */
import type { Look } from '../../looks/types';
import { motionKeys } from '../../motions';
import type { Skeleton } from '../skeleton';
import { REST_FACES, type FaceExpr, type RestFace } from './faceExprB3';

declare module '../../looks/types' {
  interface Look {
    /** The resting look of the face (appearance.ts FACES id; lane FACE-EXPR). Left out: calm. */
    restFace?: string;
  }
}

/** A resting look's id, or calm for anything else. */
export const restOf = (id: string | undefined): RestFace =>
  (REST_FACES as readonly string[]).includes(id ?? '') ? (id as RestFace) : 'calm';

/** The player's looks (every `hero…` look: the starters, their palm variants and the school uniforms) rest as chosen. */
export function applyRestFace(looks: Record<string, Look>, face: string | undefined): void {
  const rest = restOf(face);
  for (const [id, look] of Object.entries(looks)) if (id.startsWith('hero')) look.restFace = rest;
}

const KEYS: Record<string, FaceExpr> = {};
const put = (e: FaceExpr, keys: string) => {
  for (const k of keys.split(' ')) KEYS[k] = e;
};
// Blows landing (rig/receive.ts, the old hurt keys and the wuxia reels).
put(
  'pain',
  'hurt blockHit rcvBrace rcvSnap rcvReel rcvSnap2 rcvReel2 rcvJolt rcvFlung rcvFlungPeak rcvFlungDrop rcvTwist ' +
    'rcvWhirl rcvWhirl2 rcvTopple rcvTopple2 rcvAirFlat rcvImpact rcvBounce rcvBuckle rcvDrop rcvLand rcvSkid ' +
    'wxReel wxReel2 wxAir',
);
// The stun's holds: the stagger, the sag, the totter and the kneel.
put('dazed', 'rcvStagger rcvSag rcvTotter rcvShake rcvKneel rcvKneelSlump rcvKneelBreath rcvKneelHold');
// Down and dead.
put('ko', 'rcvLie rcvDieSlump rcvDieLie');
// The ท่าใหญ่ (power moves and the ultimate shows).
put('rage', 'rageCoil rageRoar rageStrike wxUltCoil wxUltRise wxUltPaint1 wxUltPaint2 wxUltPaint3 wxUltPaint4 wxUltRaise wxUltCut wxUltDeep wxUltHold');
put('laugh', 'laugh');
put('sad', 'cry');
put('victory', 'cheer danceJig');
put('talk', 'lifeSitTell lifeTeach');

/** The expression of a frame from its pose, or null for today's (mapped in faceExprB3.ts). */
export function exprFor(sk: Skeleton): FaceExpr | null {
  let key: string = sk.pose;
  if (sk.pose === 'motion') {
    const { a, b, t } = motionKeys(sk.phase);
    key = t < 0.5 ? a : b;
  }
  return KEYS[key] ?? null;
}
